import { useMyUser } from '@consigli/hooks';
import { FolderOpen, TextSearch } from 'lucide-react';
import { type FC } from 'react';
import { useTranslation } from 'react-i18next';

import { EmptyState } from '@/components/empty-state';

import { NewProjectButton } from './new-project-button';
import { CREATOR_FILTER, type CreatorFilter } from './use-project-list';

interface ProjectListEmptyProps {
  /** Non-empty when the list is empty because a search matched nothing. */
  searchText: string;
  /** Anything but ALL narrows the list, so an empty result is a filtered one. */
  creatorFilter: CreatorFilter;
}

/**
 * What the redesigned project list shows in place of the grid.
 *
 * Three cases, two of which are designed: a search or filter that matched
 * nothing, no projects yet, and no projects because the user has no
 * organization. The last has no frame and stays plain by decision: its existing
 * single string, no icon, no title and no backdrop. The two designed cases both
 * carry the backdrop, which is part of the redesign's language rather than
 * something either frame draws.
 */
export const ProjectListEmpty: FC<ProjectListEmptyProps> = ({ searchText, creatorFilter }) => {
  const { user } = useMyUser();
  const { t } = useTranslation();

  // A filter that matched nothing is not the same as having no projects, and
  // offering to create one is wrong when you have plenty.
  if (searchText || creatorFilter !== CREATOR_FILTER.ALL) {
    return (
      <EmptyState
        announce
        backdrop
        description={t('project-list.no-results-description')}
        icon={<TextSearch size={24} strokeWidth={1.75} />}
        title={t('project-list.no-results-title')}
      />
    );
  }

  if (!user.organizationId) {
    return <EmptyState description={t('project-list.no-projects-access-denied')} />;
  }

  return (
    <EmptyState
      action={<NewProjectButton />}
      backdrop
      description={t('project-list.no-projects-description')}
      icon={<FolderOpen size={24} strokeWidth={1.75} />}
      title={t('project-list.no-projects-title')}
    />
  );
};
